import store from '@/store';
import imageService from '../imageService';
import apiService from '../apiService';
import workflowService from '../workflowService';
import { logServiceError, logS3UploadError, ERROR_TYPES } from './errorLoggingService';
import { setStepCount } from './workProgress';
import { dimensionsText } from './detailImageMin';

/**
 * 대표 이미지 처리 서비스
 * - xiangji-image-editor 사용
 */
const mainImageService = {
  /**
   * 메인 이미지 작업 후 상품 및 coupangDoc 업데이트 (부분 업데이트 방식)
   * @param {Object} product - 상품 객체
   * @param {Object} imageData - 이미지 작업 결과 데이터
   * @returns {Promise<boolean>} 업데이트 성공 여부
   */
  async updateProductWithCoupangDocPartial(product, selectedOption, imageData) {
    try {
      console.log('=== updateProductWithCoupangDocPartial 시작 ===');
      console.log('product._id:', product?._id);
      console.log('selectedOption:', selectedOption);
      console.log('imageData:', imageData);

      if (!product || !product._id) {
        throw new Error('유효하지 않은 상품 정보입니다');
      }

      // 점 표기법을 사용하여 업데이트할 필드 지정
      const updateFields = {
        'mainImageStatus': 'completed',
        'updatedAt': Date.now()
      };

      // 단일 옵션 처리 (MainImageEdit에서 호출되는 경우)
      if (selectedOption && imageData && !imageData.options) {
        console.log('단일 옵션 처리 모드');

        // 선택된 옵션의 인덱스 찾기
        const optionIndex = product.options.findIndex(opt =>
          opt.optionNumber === selectedOption.optionNumber);

        console.log('optionIndex:', optionIndex);

        if (optionIndex !== -1) {
          // 메인 이미지 상태 업데이트 (점 표기법 사용)
          updateFields[`options.${optionIndex}.mainImageStatus`] = {
            status: 'completed',
            image: imageData.originalUrl || selectedOption.mainImageStatus?.image,
            processedImage: imageData.processedUrl,
            textRemoved: imageData.textRemoved || false,
            dimensions: imageData.dimensions || '',
            filesize: imageData.filesize || '', // 못 잼 = 빈 칸(지어낸 기본 파일 크기 0)
            requestId: imageData.requestId || ''
          };

          console.log('옵션 업데이트 필드:', updateFields[`options.${optionIndex}.mainImageStatus`]);

          // coupangDoc 업데이트 (해당 옵션만)
          if (product.coupangDoc && product.coupangDoc.jsonDocument &&
              product.coupangDoc.jsonDocument[optionIndex]) {

            console.log('coupangDoc 업데이트 시작 - optionIndex:', optionIndex);
            console.log('기존 coupangDoc.jsonDocument[optionIndex]:', product.coupangDoc.jsonDocument[optionIndex]);

            const docItem = product.coupangDoc.jsonDocument[optionIndex];

            if (docItem.imagePage) {
              console.log('imagePage 존재함');

              // 이미지 페이지가 있는 경우
              if (!docItem.imagePage.images) {
                console.log('images 객체가 없어서 생성');
                // 점 표기법으로 빈 객체 설정
                updateFields[`coupangDoc.jsonDocument.${optionIndex}.imagePage.images`] = {};
              }

              // 메인 이미지 URL 업데이트 (점 표기법 사용)
              const mainImageUrl = imageData.processedUrl || imageData.originalUrl;
              updateFields[`coupangDoc.jsonDocument.${optionIndex}.imagePage.images.mainImage`] = mainImageUrl;

              console.log('coupangDoc 메인 이미지 업데이트:', mainImageUrl);
            } else {
              console.log('imagePage가 없음');
            }
          } else {
            console.log('coupangDoc 또는 jsonDocument가 없음');
            console.log('product.coupangDoc 존재:', !!product.coupangDoc);
            console.log('jsonDocument 존재:', !!(product.coupangDoc && product.coupangDoc.jsonDocument));
            console.log('jsonDocument 길이:', product.coupangDoc?.jsonDocument?.length);
          }
        } else {
          console.warn('선택된 옵션을 찾을 수 없음:', selectedOption.optionNumber);
        }
      }
      // 다중 옵션 처리 (processAllOptions에서 호출되는 경우)
      else if (imageData.options && imageData.options.length > 0) {
        console.log('다중 옵션 처리 모드');

        // 옵션별 이미지 정보 매핑
        imageData.options.forEach(imgOption => {
          const optionIndex = product.options.findIndex(opt =>
            opt.optionNumber === imgOption.optionNumber);

          if (optionIndex !== -1) {
            // 메인 이미지 상태 업데이트 (점 표기법 사용)
            updateFields[`options.${optionIndex}.mainImageStatus`] = {
              status: 'completed',
              image: imgOption.originalImage || product.options[optionIndex].mainImageStatus?.image,
              processedImage: imgOption.processedImage,
              textRemoved: true,
              dimensions: imgOption.dimensions || '',
              filesize: imgOption.filesize || '' // 못 잼 = 빈 칸(지어낸 기본 파일 크기 0)
            };
          }
        });

        // coupangDoc 업데이트 (있는 경우)
        if (product.coupangDoc && product.coupangDoc.jsonDocument) {
          // 모든 jsonDocument 항목에 이미지 정보 적용
          product.coupangDoc.jsonDocument.forEach((docItem, docIndex) => {
            // 해당 옵션의 이미지 데이터 찾기
            const optionImageData = imageData.options && imageData.options[docIndex];

            if (docItem.imagePage && optionImageData && optionImageData.processedImage) {
              // 이미지 페이지가 있고 처리된 이미지가 있는 경우
              if (!docItem.imagePage.images) {
                // 메모리 상에서 객체 생성
                docItem.imagePage.images = {};
                // 점 표기법으로 빈 객체 설정
                updateFields[`coupangDoc.jsonDocument.${docIndex}.imagePage.images`] = {};
              }

              // 메인 이미지 URL 업데이트 (점 표기법 사용)
              updateFields[`coupangDoc.jsonDocument.${docIndex}.imagePage.images.mainImage`] =
                optionImageData.processedImage;
            }
          });
        }
      } else {
        console.warn('imageData 형식이 올바르지 않음:', imageData);
      }

      console.log('최종 updateFields:', updateFields);

      // 서버에 부분 업데이트 요청
      console.log('서버 업데이트 요청 시작');
      await apiService.updateRocketProductPartial(product._id, updateFields);
      console.log('서버 업데이트 완료');

      // store 상태 부분 업데이트
      console.log('store 업데이트 시작');
      store.commit('updateProductPartial', {
        productId: product._id,
        updateFields: updateFields
      });
      console.log('store 업데이트 완료');

      console.log('=== updateProductWithCoupangDocPartial 완료 ===');
      return true;
    } catch (error) {
      console.error('메인 이미지 정보로 상품 업데이트 중 오류:', error);
      throw error;
    }
  },

  /**
   * 메인 이미지 작업 후 상품 및 coupangDoc 업데이트 (이전 방식 - 호환성 유지)
   * @param {Object} product - 상품 객체
   * @param {Object} imageData - 이미지 작업 결과 데이터
   * @returns {Promise<Object>} 업데이트된 상품 객체
   * @deprecated 새로운 updateProductWithCoupangDocPartial 메서드를 사용하세요
   */
  async updateProductWithCoupangDoc(product, imageData) {
    console.warn('updateProductWithCoupangDoc는 더 이상 사용되지 않습니다. updateProductWithCoupangDocPartial을 사용하세요.');
    return this.updateProductWithCoupangDocPartial(product, imageData);
  },
    // 이미지 URL을 키로 사용하여 처리된 이미지 결과를 캐싱하는 맵
    processedImagesCache: new Map(),
    // 번역 요청 ID를 저장하는 맵
    requestIdMap: new Map(),

    /**
     * 모든 옵션의 대표 이미지를 처리합니다.
     * @param {Object} product - 처리할 상품 객체
     * @returns {Promise<Object>} 처리 결과
     */
    async processAllOptions(product) {
        console.log('메인 이미지 처리 시작:', product);
        if (!product || !product.options || product.options.length === 0) {
            console.error('처리할 옵션이 없습니다.');
            return {
                status: 'error',
                type: 'mainImage',
                error: '처리할 옵션이 없습니다.'
            };
        }

        // 로딩 상태 설정
        store.dispatch('setProcessing', {
            isProcessing: true,
            message: '대표 이미지 처리 중...'
        });

        const totalOptions = product.options.length;
        let completedOptions = 0;
        let failedOptions = 0;

        try {
            // 먼저 중복 이미지 식별
            const optionsByImage = this.groupOptionsByImage(product.options);
            console.log(`${Object.keys(optionsByImage).length}개의 고유 이미지가 있습니다.`);

            // 각 고유 이미지에 대해 처리 — 몇 장째인지 남긴다(작업개시 진행 보고가 읽는다)
            const imageEntries = Object.entries(optionsByImage);
            let imageDone = 0;
            setStepCount(product.registrationId, 'mainImage', 0, imageEntries.length);
            for (const [imageUrl, options] of imageEntries) {
                console.log(`이미지 URL(${imageUrl})에 해당하는 ${options.length}개 옵션 처리 중...`);

                try {
                    // 이미 처리된 캐시가 있는지 확인
                    let imageData = null;
                    if (this.processedImagesCache.has(imageUrl)) {
                        console.log(`캐시된 이미지 결과 사용: ${imageUrl}`);
                        imageData = this.processedImagesCache.get(imageUrl);
                    }
                    // 첫 번째 옵션의 이미지가 이미 처리되었는지 확인
                    else if (options[0].mainImageStatus &&
                        options[0].mainImageStatus.status === 'completed' &&
                        options[0].mainImageStatus.processedImage) {
                        console.log(`옵션 ${options[0].optionNumber}의 이미 처리된 이미지 사용`);
                        imageData = {
                            originalUrl: imageUrl,
                            processedUrl: options[0].mainImageStatus.processedImage,
                            dimensions: options[0].mainImageStatus.dimensions || '',
                            filesize: options[0].mainImageStatus.filesize || '', // 못 잼 = 빈 칸(지어낸 기본 파일 크기 0)
                            textRemoved: options[0].mainImageStatus.textRemoved || false,
                            requestId: options[0].mainImageStatus.requestId || '',
                            status: 'completed'
                        };
                        // 결과를 캐시에 저장
                        this.processedImagesCache.set(imageUrl, imageData);

                        // requestId가 있으면 requestIdMap에도 저장
                        if (imageData.requestId) {
                            this.requestIdMap.set(imageUrl, imageData.requestId);
                        }
                    }
                    // 새로 처리 필요
                    else {
                        console.log(`새 이미지 처리: ${imageUrl}`);

                        // 이미지 텍스트 제거 처리
                        const processResult = await this.processImage(imageUrl);
                        if (processResult) {
                            imageData = processResult;

                            // 결과를 캐시에 저장
                            this.processedImagesCache.set(imageUrl, imageData);

                            // requestId가 있으면 requestIdMap에도 저장
                            if (imageData.requestId) {
                                this.requestIdMap.set(imageUrl, imageData.requestId);
                            }
                        } else {
                            throw new Error('이미지 처리에 실패했습니다');
                        }
                    }

                    // 동일 이미지를 사용하는 모든 옵션 업데이트
                    for (const option of options) {
                        try {
                            console.log(`옵션 ${option.optionNumber}에 처리된 이미지 적용 중...`);
                            // 각 옵션별로 고유한 파일명을 생성하여 imageData 사본 생성
                            const optionImageData = { ...imageData };

                            // 옵션 이미지 업데이트 (Vuex 액션 호출)
                            await store.dispatch('updateOptionImage', {
                                registrationId: product.registrationId,
                                optionNumber: option.optionNumber,
                                imageData: optionImageData
                            });

                            // 옵션 이미지 상태만 업데이트 (coupangDoc은 나중에 일괄 업데이트)

                            completedOptions++;
                        } catch (optionError) {
                            console.error(`옵션 ${option.optionNumber} 업데이트 중 오류:`, optionError);
                            failedOptions++;
                        }
                    }
                } catch (error) {
                    console.error(`이미지 URL(${imageUrl}) 처리 중 오류:`, error);
                    // 해당 이미지를 사용하는 모든 옵션은 실패로 처리
                    failedOptions += options.length;
                }
                setStepCount(product.registrationId, 'mainImage', ++imageDone, imageEntries.length);
            }

            // 처리 결과 출력
            console.log(`메인 이미지 처리 완료: 성공 ${completedOptions}, 실패 ${failedOptions}`);

            // 제품 상태 결정
            let status = 'error';
            if (completedOptions > 0) {
                if (completedOptions === totalOptions) {
                    status = 'completed';
                } else if (failedOptions === totalOptions) {
                    status = 'error';
                } else {
                    status = 'partially_completed';
                }
            }

            // 상품 객체 업데이트
            if (completedOptions > 0) {
                const updatedProduct = store.getters.getRocketProductById(product.registrationId);
                if (updatedProduct) {
                    try {
                        // 이미지 데이터 구성
                        const imageData = {
                            options: updatedProduct.options.map(option => ({
                                optionNumber: option.optionNumber,
                                originalImage: option.mainImageStatus?.image,
                                processedImage: option.mainImageStatus?.processedImage,
                                dimensions: option.mainImageStatus?.dimensions,
                                filesize: option.mainImageStatus?.filesize
                            }))
                        };

                        // 서비스에서 직접 부분 업데이트 (다중 옵션 모드)
                        await this.updateProductWithCoupangDocPartial(updatedProduct, null, imageData);
                        console.log('메인 이미지 정보로 상품 업데이트 완료');
                    } catch (updateError) {
                        console.error('메인 이미지 정보로 상품 업데이트 실패:', updateError);
                    }
                }
            }

            return {
                status,
                type: 'mainImage',
                completedOptions,
                totalOptions,
                failedOptions
            };
        } catch (error) {
            console.error('메인 이미지 처리 중 일반 오류:', error);
            return {
                status: 'error',
                type: 'mainImage',
                error: error.message
            };
        } finally {
            // 로딩 상태 해제
            store.dispatch('setProcessing', {
                isProcessing: false,
                message: ''
            });
        }
    },

    /**
     * 옵션을 이미지 URL 기준으로 그룹화합니다.
     * @param {Array} options - 옵션 배열
     * @returns {Object} 이미지 URL별로 그룹화된 옵션 맵
     */
    groupOptionsByImage(options) {
        const groups = {};

        options.forEach(option => {
            if (option.mainImageStatus && option.mainImageStatus.image) {
                const imageUrl = option.mainImageStatus.image;
                if (!groups[imageUrl]) {
                    groups[imageUrl] = [];
                }
                groups[imageUrl].push(option);
            }
        });

        return groups;
    },

    /**
     * AI를 통해 이미지를 분석합니다 (누끼이미지 여부, 텍스트 포함 여부)
     * @param {string} imageUrl - 분석할 이미지 URL
     * @returns {Promise<Object>} 분석 결과 { isMatting: boolean, hasText: boolean }
     */
    async analyzeImageWithAI(imageUrl) {
        console.log('AI 이미지 분석 시작:', imageUrl);

        try {
            const prompt = `이 이미지를 분석해주세요:
1. 배경이 투명하거나 제거된 누끼 이미지인가요? (true/false)
2. 이미지에 텍스트(글자)가 포함되어 있나요? (true/false)

다음 JSON 형식으로만 응답해주세요:
{
  "isMatting": true 또는 false,
  "hasText": true 또는 false
}`;

            const result = await workflowService.requestExtensionWithPromise(
                {
                    workType: 'IMAGE_ANALYSIS',
                    requestArr: [{
                        prompt: prompt,
                        images: [imageUrl]
                    }]
                },
                {
                    timeout: 30000,
                    maxRetries: 2
                }
            );

            console.log('AI 이미지 분석 결과:', result);

            // 결과 파싱
            if (result && result.result) {
                const analysisResult = typeof result.result === 'string'
                    ? JSON.parse(result.result)
                    : result.result;

                return {
                    isMatting: analysisResult.isMatting || false,
                    hasText: analysisResult.hasText || false
                };
            }

            // 기본값 반환 (분석 실패 시)
            return {
                isMatting: false,
                hasText: true
            };
        } catch (error) {
            console.error('AI 이미지 분석 오류:', error);
            // 오류 발생 시 기본값 반환
            return {
                isMatting: false,
                hasText: true
            };
        }
    },

    /**
     * 단일 이미지를 처리합니다.
     * @param {string} imageUrl - 처리할 이미지 URL
     * @returns {Promise<Object>} 처리 결과
     */
    async processImage(imageUrl) {
        console.log('이미지 처리 시작:', imageUrl);

        try {
            // 누끼이미지 자동적용 설정 확인
            const storeSettings = store.state.settings[0] || {};
            const settings = storeSettings.defaultSettings || storeSettings;
            const autoApplyMatting = settings.imageSettings?.autoApplyMatting || false;

            console.log('누끼이미지 자동적용 설정:', autoApplyMatting);

            // 누끼이미지 자동적용이 ON인 경우
            if (autoApplyMatting) {
                console.log('누끼이미지 자동적용 모드 - AI 이미지 분석 시작');

                // AI로 이미지 분석
                const analysis = await this.analyzeImageWithAI(imageUrl);
                console.log('이미지 분석 결과:', analysis);

                // requestId 저장 변수
                let requestId = '';

                // 텍스트가 있으면 텍스트 제거
                if (analysis.hasText) {
                    console.log('텍스트 포함 이미지 - 텍스트 제거 진행');
                    const textRemovalResult = await imageService.removeTextFromImage(imageUrl);

                    if (textRemovalResult && textRemovalResult.RmUrl) {
                        imageUrl = textRemovalResult.RmUrl;
                        requestId = textRemovalResult.requestId || '';  // requestId 저장
                        console.log('텍스트 제거 완료, 새 이미지 URL:', imageUrl);
                        console.log('텍스트 제거 requestId:', requestId);
                    }
                }

                // 누끼이미지가 아니면 배경 제거
                if (!analysis.isMatting) {
                    console.log('일반 이미지 - 배경 제거 진행');

                    // 배경 제거 API 호출 (server.js의 removeImageBackground 함수 사용)
                    try {
                        const mattingResult = await apiService.removeImageBackground({
                            imageUrl: imageUrl,
                            bgColor: '255,255,255'  // 흰색 배경
                        });

                        if (mattingResult && mattingResult.url) {
                            imageUrl = mattingResult.url;
                            // 배경 제거의 requestId도 저장 (텍스트 제거 requestId가 없는 경우)
                            if (!requestId && mattingResult.requestId) {
                                requestId = mattingResult.requestId;
                            }
                            console.log('배경 제거 완료, 새 이미지 URL:', imageUrl);
                            console.log('배경 제거 requestId:', mattingResult.requestId);
                        }
                    } catch (mattingError) {
                        console.error('배경 제거 실패:', mattingError);
                        // 배경 제거 실패 시 원본 이미지 사용
                        logServiceError('mainImageService', 'processImage', mattingError, {
                            errorType: ERROR_TYPES.BACKGROUND_REMOVAL_FAILED,
                            fallbackAction: '원본 이미지 사용',
                            additional: { imageUrl: imageUrl?.substring(0, 100) }
                        });
                    }
                }

                // 처리된 이미지로 최종 결과 생성
                let dimensions = await this.getImageDimensions(imageUrl);

                // S3에 업로드
                let s3ImageUrl = '';
                try {
                    const imageBlob = await imageService.downloadImage(imageUrl);
                    const base64Data = await imageService.blobToBase64(imageBlob);
                    const supplierCode = store.state.user.suppliercode || store.state.user.companyid || 'default';
                    const fileName = `${supplierCode}_main_processed_${Date.now()}.jpg`;

                    const uploadResult = await imageService.uploadImageToS3(
                        base64Data,
                        fileName,
                        'main'
                    );

                    if (uploadResult && typeof uploadResult === 'object') {
                        s3ImageUrl = uploadResult.imageUrl || imageUrl;
                        if (uploadResult.width && uploadResult.height) dimensions = { width: uploadResult.width, height: uploadResult.height };   // 서버가 400~8000px 로 맞춘 실제 크기(PEACH 360×360 반려)
                    } else if (typeof uploadResult === 'string') {
                        s3ImageUrl = uploadResult;
                    } else {
                        s3ImageUrl = imageUrl;
                    }
                } catch (uploadError) {
                    console.error('S3 업로드 실패:', uploadError);
                    s3ImageUrl = imageUrl;
                }

                return {
                    originalUrl: imageUrl,
                    processedUrl: s3ImageUrl || imageUrl,
                    dimensions: dimensionsText(dimensions),
                    filesize: '알 수 없음',
                    textRemoved: analysis.hasText,
                    backgroundRemoved: !analysis.isMatting,
                    requestId: requestId,  // 저장된 requestId 사용
                    status: 'completed'
                };
            }

            // 누끼이미지 자동적용이 OFF인 경우 - 기존 프로세스 (텍스트 제거만)
            console.log('기본 모드 - 텍스트 제거만 진행');
            const result = await imageService.removeTextFromImage(imageUrl);

            // 결과가 없거나 에러가 발생하면 원본 이미지를 사용하는 기본 데이터 반환
            if (!result) {
                console.warn('텍스트 제거 결과가 없습니다. 원본 이미지를 사용합니다.');

                // 이미지 크기 가져오기
                let dimensions = await this.getImageDimensions(imageUrl);

                // 원본 이미지 S3에 업로드
                let s3ImageUrl = '';
                try {
                    // 이미지 blob으로 다운로드
                    const imageBlob = await imageService.downloadImage(imageUrl);
                    const base64Data = await imageService.blobToBase64(imageBlob);

                    // 파일명 생성
                    const supplierCode = store.state.user.suppliercode || store.state.user.companyid || 'default';
                    const fileName = `${supplierCode}_main_${Date.now()}.jpg`;

                    // S3에 업로드
                    const uploadResult = await imageService.uploadImageToS3(
                        base64Data,
                        fileName,
                        'main'
                    );

                    // 업로드 결과 처리 (객체 또는 문자열)
                    if (uploadResult && typeof uploadResult === 'object') {
                        // 객체 형태로 반환된 경우 (신규 형식)
                        if (uploadResult.imageUrl) {
                            s3ImageUrl = uploadResult.imageUrl;
                            if (uploadResult.width && uploadResult.height) dimensions = { width: uploadResult.width, height: uploadResult.height };   // 서버가 400~8000px 로 맞춘 실제 크기(PEACH 360×360 반려)
                        } else {
                            console.error('원본 이미지 업로드 결과에 imageUrl이 없습니다:', uploadResult);
                            s3ImageUrl = imageUrl; // 실패 시 원본 URL 사용
                        }
                    } else if (typeof uploadResult === 'string') {
                        // 문자열로 반환된 경우 (이전 형식)
                        s3ImageUrl = uploadResult;
                    } else {
                        console.error('원본 이미지 알 수 없는 업로드 결과 형식:', uploadResult);
                        s3ImageUrl = imageUrl; // 실패 시 원본 URL 사용
                    }

                    console.log('원본 이미지 S3 업로드 완료:', s3ImageUrl);
                } catch (uploadError) {
                    console.error('원본 이미지 S3 업로드 실패:', uploadError);
                    s3ImageUrl = imageUrl; // 실패 시 원본 URL 사용
                    logS3UploadError('mainImageService', 'processImage', uploadError, {
                        fallbackAction: '원본 URL 사용',
                        additional: { imageType: 'original', imageUrl: imageUrl?.substring(0, 100) }
                    });
                }

                // 이미지 데이터 구성 (원본 이미지 사용)
                return {
                    originalUrl: imageUrl,
                    processedUrl: s3ImageUrl || imageUrl, // S3 URL 또는 원본 URL
                    dimensions: dimensionsText(dimensions),
                    filesize: '알 수 없음',
                    textRemoved: false,
                    requestId: '', // 빈 requestId
                    status: 'completed'
                };
            }

            console.log('텍스트 제거 결과:', result);

            // 텍스트가 제거된 이미지 URL
            const processedImageUrl = result.RmUrl || result.url || imageUrl;
            const requestId = result.requestId || '';

            // 이미지 크기 가져오기
            let dimensions = await this.getImageDimensions(processedImageUrl);

            // 처리된 이미지 S3에 업로드
            let s3ImageUrl = '';
            try {
                // 이미지 blob으로 다운로드
                const imageBlob = await imageService.downloadImage(processedImageUrl);
                const base64Data = await imageService.blobToBase64(imageBlob);

                // 파일명 생성
                const supplierCode = store.state.user.suppliercode || store.state.user.companyid || 'default';
                const fileName = `${supplierCode}_main_processed_${Date.now()}.jpg`;

                // S3에 업로드
                const uploadResult = await imageService.uploadImageToS3(
                    base64Data,
                    fileName,
                    'main'
                );

                // 업로드 결과 처리 (객체 또는 문자열)
                if (uploadResult && typeof uploadResult === 'object') {
                    // 객체 형태로 반환된 경우 (신규 형식)
                    if (uploadResult.imageUrl) {
                        s3ImageUrl = uploadResult.imageUrl;
                        if (uploadResult.width && uploadResult.height) dimensions = { width: uploadResult.width, height: uploadResult.height };   // 서버가 400~8000px 로 맞춘 실제 크기(PEACH 360×360 반려)
                    } else {
                        console.error('처리된 이미지 업로드 결과에 imageUrl이 없습니다:', uploadResult);
                        s3ImageUrl = processedImageUrl; // 실패 시 처리된 URL 사용
                    }
                } else if (typeof uploadResult === 'string') {
                    // 문자열로 반환된 경우 (이전 형식)
                    s3ImageUrl = uploadResult;
                } else {
                    console.error('처리된 이미지 알 수 없는 업로드 결과 형식:', uploadResult);
                    s3ImageUrl = processedImageUrl; // 실패 시 처리된 URL 사용
                }

                console.log('처리된 이미지 S3 업로드 완료:', s3ImageUrl);
            } catch (uploadError) {
                console.error('처리된 이미지 S3 업로드 실패:', uploadError);
                s3ImageUrl = processedImageUrl; // 실패 시 처리된 URL 사용
                logS3UploadError('mainImageService', 'processImage', uploadError, {
                    fallbackAction: '처리된 URL 사용',
                    additional: { imageType: 'processed', imageUrl: imageUrl?.substring(0, 100) }
                });
            }

            // 이미지 데이터 구성
            const imageData = {
                originalUrl: imageUrl,
                processedUrl: s3ImageUrl || processedImageUrl, // S3 URL 또는 처리된 URL
                dimensions: dimensionsText(dimensions),
                filesize: '알 수 없음', // 외부 URL이라 크기를 알 수 없음
                textRemoved: !!result.RmUrl,
                requestId: requestId,
                status: 'completed'
            };

            return imageData;
        } catch (error) {
            console.error('이미지 처리 중 오류:', error);

            // 오류 발생 시에도 원본 이미지를 사용하는 기본 데이터 반환
            try {
                // 이미지 크기 가져오기
                let dimensions = await this.getImageDimensions(imageUrl);

                // 원본 이미지 S3에 업로드 시도
                let s3ImageUrl = '';
                try {
                    // 이미지 blob으로 다운로드
                    const imageBlob = await imageService.downloadImage(imageUrl);
                    const base64Data = await imageService.blobToBase64(imageBlob);

                    // 파일명 생성
                    const supplierCode = store.state.user.suppliercode || store.state.user.companyid || 'default';
                    const fileName = `${supplierCode}_main_fallback_${Date.now()}.jpg`;

                    // S3에 업로드
                    const uploadResult = await imageService.uploadImageToS3(
                        base64Data,
                        fileName,
                        'main'
                    );

                    // 업로드 결과 처리 (객체 또는 문자열)
                    if (uploadResult && typeof uploadResult === 'object') {
                        // 객체 형태로 반환된 경우 (신규 형식)
                        if (uploadResult.imageUrl) {
                            s3ImageUrl = uploadResult.imageUrl;
                            if (uploadResult.width && uploadResult.height) dimensions = { width: uploadResult.width, height: uploadResult.height };   // 서버가 400~8000px 로 맞춘 실제 크기(PEACH 360×360 반려)
                        } else {
                            console.error('폴백 이미지 업로드 결과에 imageUrl이 없습니다:', uploadResult);
                            s3ImageUrl = imageUrl; // 실패 시 원본 URL 사용
                        }
                    } else if (typeof uploadResult === 'string') {
                        // 문자열로 반환된 경우 (이전 형식)
                        s3ImageUrl = uploadResult;
                    } else {
                        console.error('폴백 이미지 알 수 없는 업로드 결과 형식:', uploadResult);
                        s3ImageUrl = imageUrl; // 실패 시 원본 URL 사용
                    }

                    console.log('폴백 이미지 S3 업로드 완료:', s3ImageUrl);
                } catch (uploadError) {
                    console.error('폴백 이미지 S3 업로드 실패:', uploadError);
                    s3ImageUrl = imageUrl; // 실패 시 원본 URL 사용
                }

                // 원본 이미지 사용
                return {
                    originalUrl: imageUrl,
                    processedUrl: s3ImageUrl || imageUrl, // S3 URL 또는 원본 URL
                    dimensions: dimensionsText(dimensions),
                    filesize: '알 수 없음',
                    textRemoved: false,
                    requestId: '',
                    status: 'error'
                };
            } catch (dimensionError) {
                // 이미지 크기 가져오기도 실패한 경우
                return {
                    originalUrl: imageUrl,
                    processedUrl: imageUrl, // 원본 URL 그대로 사용
                    dimensions: '', // 못 잼(지어낸 크기 0)
                    filesize: '알 수 없음',
                    textRemoved: false,
                    requestId: '',
                    status: 'error'
                };
            }
        }
    },

    /**
     * 이미지 크기 가져오기
     * @param {string} imageUrl - 이미지 URL
     * @returns {Promise<{width: number, height: number}|null>} - 이미지 크기(못 잼 = null)
     */
    getImageDimensions(imageUrl) {
        return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => {
                resolve({
                    width: img.naturalWidth,
                    height: img.naturalHeight
                });
            };
            img.onerror = (err) => {
                // 못 잼 = null(800×800 지어내기 0) — 기록 크기 칸은 비고, 보내기 전 크기 문(rocket-submit)이 다시 잰다
                console.error('대표이미지 크기 못 잼:', imageUrl, err);
                resolve(null);
            };
            img.src = imageUrl;
        });
    },

    /**
     * CoupangDoc 업데이트 (부분 업데이트 방식 사용)
     * @param {Object} option - 업데이트할 옵션
     * @param {Object} imageData - 처리된 이미지 데이터
     * @returns {Promise<boolean>} 업데이트 성공 여부
     */
    async updateCoupangDoc(option, imageData) {
        try {
            console.log('updateCoupangDoc 호출 (부분 업데이트 방식)');

            // imageData가 유효한지 확인
            if (!imageData || !imageData.processedUrl) {
                console.warn('유효하지 않은 이미지 데이터:', imageData);
                return false;
            }

            // 옵션 번호가 유효한지 확인
            if (!option || !option.optionNumber) {
                console.warn('유효하지 않은 옵션 데이터:', option);
                return false;
            }

            // 이미지 처리 성공 후, 옵션이 업데이트된 최신 상태 가져오기
            const updatedOption = store.getters.getOptionByNumber(option.optionNumber);
            if (!updatedOption) {
                console.warn(`옵션 번호 ${option.optionNumber}에 해당하는 옵션을 찾을 수 없습니다.`);
                return false;
            }

            // 상품 정보 가져오기
            const product = store.getters.getRocketProductById(updatedOption.registrationId);
            if (!product) {
                console.warn(`상품 ID ${updatedOption.registrationId}에 해당하는 상품을 찾을 수 없습니다.`);
                return false;
            }

            // 부분 업데이트를 위한 이미지 데이터 구성
            const optionIndex = product.options.findIndex(opt => opt.optionNumber === option.optionNumber);
            if (optionIndex === -1) {
                console.warn(`상품에서 옵션 번호 ${option.optionNumber}를 찾을 수 없습니다.`);
                return false;
            }

            // 점 표기법을 사용하여 업데이트할 필드 지정
            const updateFields = {
                [`options.${optionIndex}.mainImageStatus`]: {
                    status: 'completed',
                    image: updatedOption.mainImageStatus?.image || imageData.originalUrl,
                    processedImage: imageData.processedUrl,
                    textRemoved: imageData.textRemoved || false,
                    dimensions: imageData.dimensions || '',
                    filesize: imageData.filesize || '', // 못 잼 = 빈 칸(지어낸 기본 파일 크기 0)
                    requestId: imageData.requestId || ''
                },
                'updatedAt': Date.now()
            };

            // 상품 레벨의 coupangDoc 업데이트 (있는 경우)
            if (product.coupangDoc && product.coupangDoc.jsonDocument) {
                console.log(`상품 레벨 coupangDoc 업데이트 - 옵션 인덱스: ${optionIndex}`);

                // 해당 옵션 인덱스에 맞는 jsonDocument 항목 찾기
                if (product.coupangDoc.jsonDocument.length > optionIndex) {
                    const docItem = product.coupangDoc.jsonDocument[optionIndex];

                    if (docItem && docItem.imagePage) {
                        // 이미지 페이지가 있는 경우
                        if (!docItem.imagePage.images) {
                            // 메모리 상에서 객체 생성
                            docItem.imagePage.images = {};
                            // 점 표기법으로 빈 객체 설정
                            updateFields[`coupangDoc.jsonDocument.${optionIndex}.imagePage.images`] = {};
                        }

                        // 메인 이미지 URL 업데이트 (점 표기법 사용)
                        updateFields[`coupangDoc.jsonDocument.${optionIndex}.imagePage.images.mainImage`] =
                            imageData.processedUrl;

                        console.log(`상품 coupangDoc의 jsonDocument[${optionIndex}].imagePage.images.mainImage 업데이트`);
                    } else {
                        console.warn(`상품 coupangDoc의 jsonDocument[${optionIndex}]에 imagePage가 없습니다.`);
                    }
                } else {
                    console.warn(`상품 coupangDoc의 jsonDocument 배열 길이(${product.coupangDoc.jsonDocument.length})가 옵션 인덱스(${optionIndex})보다 작습니다.`);
                }
            } else {
                console.warn('상품에 coupangDoc이 없거나 jsonDocument가 없습니다.');
            }

            // 서버에 부분 업데이트 요청
            await apiService.updateRocketProductPartial(product._id, updateFields);

            // store 상태 부분 업데이트
            store.commit('updateProductPartial', {
                productId: product._id,
                updateFields: updateFields
            });

            console.log(`옵션 ${option.optionNumber}의 이미지 정보 부분 업데이트 완료`);
            return true;
        } catch (error) {
            console.error('CoupangDoc 업데이트 오류:', error);
            return false;
        }
    },

    /**
     * 옵션의 대표 이미지를 처리합니다.
     * @param {Object} option - 처리할 옵션 객체
     * @returns {Promise<Object>} 처리 결과
     */
    async processOption(option, registrationId) {
        console.log('옵션 이미지 처리 시작:', option);
        console.log('선택된 옵션 번호:', option.optionNumber);

        if (!option || !option.mainImageStatus || !option.mainImageStatus.image) {
            console.error('처리할 이미지가 없습니다.');
            return {
                status: 'error',
                error: '처리할 이미지가 없습니다.'
            };
        }

        // 로딩 상태 설정
        store.dispatch('setProcessing', {
            isProcessing: true,
            message: '대표 이미지 처리 중...'
        });

        const imageUrl = option.mainImageStatus.image;
        console.log('처리할 이미지 URL:', imageUrl);

        // 캐시 상태 확인
        console.log('캐시 상태:', {
            'processedImagesCache 크기': this.processedImagesCache.size,
            '해당 이미지 캐시 있음': this.processedImagesCache.has(imageUrl),
            'requestIdMap 크기': this.requestIdMap.size,
            '해당 이미지 requestId 있음': this.requestIdMap.has(imageUrl)
        });

        try {
            // 캐시에 이미 처리된 이미지가 있는지 확인
            if (this.processedImagesCache.has(imageUrl)) {
                console.log(`캐시된 이미지 결과 사용: ${imageUrl}`);
                const cachedImageData = this.processedImagesCache.get(imageUrl);

                // 캐시된 이미지 데이터 검증
                console.log('캐시된 이미지 데이터:', cachedImageData);

                // 깊은 복사 수행
                const imageDataCopy = { ...cachedImageData };
                console.log('복사된 이미지 데이터:', imageDataCopy);

                // 필수 필드 확인 및 설정
                if (!imageDataCopy.processedUrl) {
                    console.warn('캐시된 데이터에 processedUrl이 없습니다. 원본 이미지로 대체합니다.');
                    imageDataCopy.processedUrl = imageUrl;
                }

                // 옵션 이미지 업데이트 (Vuex 액션 호출)
                try {
                    console.log('updateOptionImage 액션 호출:', {
                        옵션번호: option.optionNumber,
                        이미지데이터: imageDataCopy
                    });

                    await store.dispatch('updateOptionImage', {
                        registrationId,
                        optionNumber: option.optionNumber,
                        imageData: imageDataCopy
                    });

                    console.log('updateOptionImage 액션 완료');
                } catch (updateError) {
                    console.error('updateOptionImage 액션 오류:', updateError);
                }

                // 옵션 이미지 상태만 업데이트 (coupangDoc은 updateProductWithCoupangDocPartial에서 일괄 업데이트)
                console.log('옵션 이미지 상태 업데이트 완료 - coupangDoc은 별도로 업데이트됩니다.');

                // 결과 반환
                const result = {
                    status: 'completed',
                    processedUrl: imageDataCopy.processedUrl,
                    dimensions: imageDataCopy.dimensions,
                    filesize: imageDataCopy.filesize,
                    requestId: imageDataCopy.requestId,
                    textRemoved: imageDataCopy.textRemoved
                };

                console.log('processOption 캐시 사용 결과:', result);
                return result;
            }

            // 캐시에 없는 경우 이미지 처리
            console.log('캐시에 이미지가 없음. processImage 호출...');
            let imageData;
            try {
                imageData = await this.processImage(imageUrl);
                console.log('processImage 결과:', imageData);
            } catch (processError) {
                console.error('processImage 오류:', processError);
                // 오류 발생 시 기본 이미지 데이터 생성
                imageData = {
                    originalUrl: imageUrl,
                    processedUrl: imageUrl,
                    dimensions: '', // 못 잼(지어낸 크기 0)
                    filesize: '알 수 없음',
                    textRemoved: false,
                    requestId: '',
                    status: 'error'
                };
            }

            // processImage는 항상 값을 반환하므로 이 체크는 필요 없지만 안전을 위해 유지
            if (!imageData || !imageData.processedUrl) {
                console.warn('이미지 처리 결과가 유효하지 않아 원본 이미지를 사용합니다.');
                // 기본 이미지 데이터 생성 (원본 이미지 사용)
                const defaultImageData = {
                    originalUrl: imageUrl,
                    processedUrl: imageUrl,
                    dimensions: '', // 못 잼(지어낸 크기 0)
                    filesize: '알 수 없음',
                    textRemoved: false,
                    requestId: '',
                    status: 'completed'
                };

                console.log('기본 이미지 데이터 생성:', defaultImageData);

                // 결과를 캐시에 저장
                this.processedImagesCache.set(imageUrl, defaultImageData);
                console.log('기본 이미지 데이터를 캐시에 저장 완료');

                // 옵션 이미지 업데이트 (Vuex 액션 호출)
                try {
                    console.log('updateOptionImage 호출 (기본 데이터):', {
                        옵션번호: option.optionNumber,
                        이미지데이터: defaultImageData
                    });

                    await store.dispatch('updateOptionImage', {
                        registrationId,
                        optionNumber: option.optionNumber,
                        imageData: defaultImageData
                    });

                    console.log('updateOptionImage 완료 (기본 데이터)');
                } catch (updateError) {
                    console.error('updateOptionImage 오류 (기본 데이터):', updateError);
                }

                // 옵션 이미지 상태만 업데이트 (coupangDoc은 updateProductWithCoupangDocPartial에서 일괄 업데이트)
                console.log('옵션 이미지 상태 업데이트 완료 (기본 데이터) - coupangDoc은 별도로 업데이트됩니다.');

                const result = {
                    status: 'completed_with_original',
                    processedUrl: imageUrl,
                    originalUrl: imageUrl,
                    dimensions: '', // 못 잼(지어낸 크기 0)
                    filesize: '알 수 없음',
                    textRemoved: false,
                    requestId: ''
                };

                console.log('processOption 결과 (기본 데이터):', result);
                return result;
            }

            // 결과를 캐시에 저장
            console.log('이미지 데이터를 캐시에 저장:', imageData);
            this.processedImagesCache.set(imageUrl, imageData);

            // requestId가 있으면 requestIdMap에도 저장
            if (imageData.requestId) {
                console.log('requestId를 캐시에 저장:', imageData.requestId);
                this.requestIdMap.set(imageUrl, imageData.requestId);
            } else {
                console.log('requestId가 없어 requestIdMap에 저장하지 않음');
            }

            // 옵션 이미지 업데이트 (Vuex 액션 호출)
            try {
                console.log('updateOptionImage 호출:', {
                    옵션번호: option.optionNumber,
                    이미지데이터: imageData
                });

                await store.dispatch('updateOptionImage', {
                    registrationId,
                    optionNumber: option.optionNumber,
                    imageData
                });

                console.log('updateOptionImage 완료');
            } catch (updateError) {
                console.error('옵션 이미지 업데이트 중 오류:', updateError);
                // 오류가 발생해도 계속 진행
            }

            // 옵션 이미지 상태만 업데이트 (coupangDoc은 updateProductWithCoupangDocPartial에서 일괄 업데이트)
            console.log('옵션 이미지 상태 업데이트 완료 - coupangDoc은 별도로 업데이트됩니다.');

            const result = {
                status: 'completed',
                processedUrl: imageData.processedUrl,
                originalUrl: imageData.originalUrl,
                requestId: imageData.requestId,
                dimensions: imageData.dimensions,
                filesize: imageData.filesize,
                textRemoved: imageData.textRemoved
            };

            console.log('processOption 최종 결과:', result);
            return result;
        } catch (error) {
            console.error('옵션 이미지 처리 중 오류:', error);
            return {
                status: 'error',
                error: error.message
            };
        } finally {
            // 로딩 상태 해제
            store.dispatch('setProcessing', {
                isProcessing: false,
                message: ''
            });
        }
    },

    /**
     * 이미지를 S3에 업로드합니다.
     * @param {Object} params - 업로드 매개변수 (imageData, filename, type)
     * @returns {Promise<string|Object>} - 업로드된 이미지 URL 또는 결과 객체
     */
    async uploadImage(params) {
        if (!params || !params.imageData) {
            throw new Error('업로드할 이미지 데이터가 없습니다.');
        }

        try {
            // imageService의 uploadImageToS3 메서드를 사용하여 업로드
            const uploadResult = await imageService.uploadImageToS3(
                params.imageData,
                params.filename || `main_${Date.now()}.jpg`,
                params.type || 'main'
            );

            // 결과 형식 확인 및 처리
            if (uploadResult && typeof uploadResult === 'object') {
                // 객체 형태로 반환된 경우 (신규 형식) - 그대로 반환
                return uploadResult.imageUrl;
            } else if (typeof uploadResult === 'string') {
                // 문자열로 반환된 경우 (이전 형식) - 그대로 반환
                return uploadResult;
            } else {
                console.error('알 수 없는 업로드 결과 형식:', uploadResult);
                throw new Error('이미지 업로드 결과 형식이 유효하지 않습니다.');
            }
        } catch (error) {
            console.error('이미지 업로드 중 오류:', error);
            throw error;
        }
    },

    /**
     * 이미지 번역 요청 (텍스트 삭제만 필요함)
     * @param {string} imageUrl - 이미지 URL
     * @returns {Promise<Object>} - 요청 결과 (requestId 포함)
     */
    async requestImageTranslation(imageUrl) {
        try {
            // 캐시에 requestId가 있는지 확인
            if (this.requestIdMap.has(imageUrl)) {
                const requestId = this.requestIdMap.get(imageUrl);
                console.log(`캐시된 요청 ID 사용: ${requestId}`);
                return { requestId, cached: true };
            }

            // 없으면 새로 요청 (번역이 아닌 텍스트 제거만)
            const result = await imageService.removeTextFromImage(imageUrl);
            console.log('requestImageTranslation 응답:', result);

            if (result && result.requestId) {
                console.log('유효한 requestId를 받음:', result.requestId);
                this.requestIdMap.set(imageUrl, result.requestId);
                return {
                    requestId: result.requestId,
                    url: result.url || result.RmUrl
                };
            } else if (result && result.url) {
                // requestId가 없지만 URL은 있는 경우
                console.log('requestId 없이 URL만 받음:', result.url);
                return {
                    url: result.url,
                    noRequestId: true
                };
            }

            console.error('이미지 처리 응답 형식 오류:', result);
            throw new Error('이미지 처리 요청에 실패했습니다.');
        } catch (error) {
            console.error('이미지 처리 요청 오류:', error);
            throw error;
        }
    },

    /**
     * 외부 이미지 에디터 초기화
     * @param {string} requestId - 번역 요청 ID
     * @param {string} imageUrl - 원본 이미지 URL
     * @returns {Promise<boolean>} 초기화 성공 여부
     */
    async initExternalEditor(requestId, imageUrl) {
        try {
            if (!requestId) {
                // 이미지 URL로 요청 ID 조회
                requestId = this.requestIdMap.get(imageUrl);

                if (!requestId) {
                    console.warn('이미지 에디터 초기화 실패: requestId가 없습니다.');
                    return false;
                }
            }

            // imageService의 외부 에디터 초기화 메서드 호출
            const result = await imageService.initExternalEditor(requestId, imageUrl);
            return !!result;
        } catch (error) {
            console.error('이미지 에디터 초기화 오류:', error);
            return false;
        }
    },

    /**
     * 캐시 데이터를 초기화합니다.
     */
    clearCache() {
        this.processedImagesCache.clear();
        this.requestIdMap.clear();
        console.log('이미지 처리 캐시가 초기화되었습니다.');
    }
};

export default mainImageService;